import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, ViewChild, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { FileUpload, FileUploadModule } from 'primeng/fileupload';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';

/**
 * Riga documento mostrata dal componente. `raw` può contenere l'oggetto originale
 * del chiamante (es. RelatedDocument / ReviewControlDocument), riemesso negli eventi.
 */
export interface DocManagerItem {
  id: string | number;
  fileName: string;
  title?: string | null;
  uploadDate?: string | null;
  fileSize?: number | null;
  raw?: unknown;
}

/**
 * Componente riutilizzabile per la gestione documenti: tabella (Nome, Titolo, Tipo, Data, Azioni)
 * + dialog di caricamento con Titolo obbligatorio e drag&drop.
 *
 * È presentazionale: non effettua chiamate HTTP. Il chiamante gestisce upload/download/delete
 * tramite gli output e passa la lista aggiornata via `documents`.
 * Passare `uploading` per mostrare lo spinner e chiudere il dialog a fine caricamento.
 */
@Component({
  selector: 'app-documents-manager',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    DialogModule,
    FileUploadModule,
    InputTextModule,
    TagModule,
    TooltipModule,
  ],
  templateUrl: './documents-manager.component.html',
  styleUrls: ['./documents-manager.component.scss'],
})
export class DocumentsManagerComponent {
  /** Documenti da mostrare in tabella. */
  @Input() documents: DocManagerItem[] = [];
  /** Nasconde upload/elimina (sola consultazione). */
  @Input() readonly = false;
  /** Tipi accettati (attributo accept del file input). */
  @Input() accept = '.pdf,application/pdf';
  /** Dimensione massima per file (byte). */
  @Input() maxFileSizeBytes = 100 * 1024 * 1024;
  /** Titolo obbligatorio in fase di caricamento. */
  @Input() titleRequired = true;
  @Input() addButtonLabel = 'Aggiungi documento';
  @Input() emptyMessage = 'Nessun documento caricato';
  /** Titolo opzionale mostrato a sinistra nella riga header (il bottone "Aggiungi" sta a destra). */
  @Input() heading?: string;
  /** Testo descrittivo opzionale sotto il titolo. */
  @Input() hint?: string;
  /**
   * Mostra la riga header interna (titolo + bottone "Aggiungi"). Impostare a false quando
   * il parent ha già un header di sezione e inserisce lì il bottone chiamando openUploadDialog().
   */
  @Input() showHeader = true;
  @Input() dialogHeader = 'Carica Nuovo Documento';
  /** Testo di aiuto formati/dimensione nel dialog. */
  @Input() formatsHint = 'PDF';
  @Input() maxSizeHint = '100 MB';

  /** Richiesta di upload: il chiamante esegue la POST e aggiorna `documents`. */
  @Output() upload = new EventEmitter<{ file: File; title: string }>();
  @Output() download = new EventEmitter<DocManagerItem>();
  @Output() remove = new EventEmitter<DocManagerItem>();

  @ViewChild('fu') fileUpload?: FileUpload;

  showUploadDialog = signal(false);
  newTitle = signal('');

  private _uploading = false;
  private pendingUpload = false;

  /** Stato di caricamento gestito dal chiamante: true durante la POST, false a fine. */
  @Input() set uploading(value: boolean) {
    const was = this._uploading;
    this._uploading = value;
    // A fine caricamento (true -> false) chiude il dialog se avevamo emesso un upload.
    if (was && !value && this.pendingUpload) {
      this.pendingUpload = false;
      this.closeUploadDialog();
    }
  }
  get uploading(): boolean {
    return this._uploading;
  }

  openUploadDialog(): void {
    this.newTitle.set('');
    this.showUploadDialog.set(true);
  }

  closeUploadDialog(): void {
    this.showUploadDialog.set(false);
    this.newTitle.set('');
    this.fileUpload?.clear();
  }

  /** Invocato dal customUpload del p-fileupload (click su "Carica"). */
  onUploadHandler(event: { files: File[] }): void {
    const file = event?.files?.[0];
    if (!file) {
      return;
    }
    if (this.titleRequired && !this.newTitle().trim()) {
      return;
    }
    this.pendingUpload = true;
    this.upload.emit({ file, title: this.newTitle().trim() });
    // Il dialog viene chiuso quando `uploading` torna false (vedi setter).
  }

  // ===== Helpers di presentazione =====

  getFileExtension(fileName: string | null | undefined): string {
    if (!fileName) return '';
    const dot = fileName.lastIndexOf('.');
    return dot >= 0 ? fileName.substring(dot + 1).toUpperCase() : '';
  }

  getFileTypeSeverity(fileName: string | null | undefined): 'danger' | 'success' | 'warn' | 'info' {
    const ext = this.getFileExtension(fileName).toLowerCase();
    if (ext === 'pdf') return 'danger';
    if (['xls', 'xlsx', 'csv'].includes(ext)) return 'success';
    if (['ppt', 'pptx'].includes(ext)) return 'warn';
    return 'info';
  }

  getFileIcon(fileName: string | null | undefined): string {
    const ext = this.getFileExtension(fileName).toLowerCase();
    if (ext === 'pdf') return 'pi pi-file-pdf';
    if (['xls', 'xlsx', 'csv'].includes(ext)) return 'pi pi-file-excel';
    if (['doc', 'docx'].includes(ext)) return 'pi pi-file-word';
    return 'pi pi-file';
  }

  formatFileSize(bytes: number | null | undefined): string {
    if (!bytes) return '';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 10) / 10 + ' ' + sizes[i];
  }
}
